/**
 * SSE helpers for Hono.
 *
 * Wraps `hono/streaming`'s `streamSSE` so controllers don't import Hono's
 * streaming utilities directly. Also re-exports `createBatchReporter` from the
 * existing SSE module (it's framework-agnostic — takes a `SSESender`).
 */

import { streamSSE, type SSEStreamingApi } from 'hono/streaming'
import type { SSESender, SSEOptions } from '@gzl10/nexus-sdk'
import type { NexusContext } from './types.js'
import { logger } from '@kernel/logger/index.js'

export { createBatchReporter } from '../core/sse/batch-reporter.js'
export type { BatchReporter } from '../core/sse/batch-reporter.js'

/**
 * Stream an SSE response from a Hono handler.
 *
 * @param c Hono context
 * @param handler Async function that receives an `SSESender` (same shape as
 *   the legacy Express helper) and emits events.
 * @param options Stream options — retry interval and keep-alive.
 */
export async function streamSse(
  c: NexusContext,
  handler: (sender: SSESender) => Promise<void>,
  options: SSEOptions = {}
): Promise<Response> {
  const { retry = 3000, keepAlive = 30000, onClose } = options

  return streamSSE(c, async (stream: SSEStreamingApi) => {
    stream.onAbort(() => {
      onClose?.()
    })

    await stream.writeSSE({ data: '', retry })

    const keepAliveTimer =
      keepAlive > 0
        ? setInterval(() => {
            stream.writeSSE({ data: ':ping', event: 'ping' }).catch((err: Error) => {
              logger.debug({ err }, 'SSE keep-alive write failed')
            })
          }, keepAlive)
        : null

    const sender: SSESender = {
      send(data: unknown, event?: string, id?: string): void {
        const payload: { data: string; event?: string; id?: string } = {
          data: typeof data === 'string' ? data : JSON.stringify(data)
        }
        if (event) payload.event = event
        if (id) payload.id = id
        stream.writeSSE(payload).catch((err: Error) => {
          logger.debug({ err }, 'SSE send failed')
        })
      },
      comment(text: string): void {
        stream.writeSSE({ data: `:${text}` }).catch(() => {})
      },
      close(): void {
        if (keepAliveTimer) clearInterval(keepAliveTimer)
        stream.close().catch(() => {})
      }
    }

    try {
      await handler(sender)
    } finally {
      if (keepAliveTimer) clearInterval(keepAliveTimer)
    }
  })
}
